import React from "react";
import {
  CalfIdentityFields,
  CalfBirthFields,
  CalfWeightMethodField,
  CalfColostrumFields,
  CalfFeedingFields,
  CalfFollowUpFields,
} from "../forms/calfFields";
import CalfFeeds from "./CalfFeeds";

const PURPOSES = { elevage: "Élevage", engraissement: "Engraissement" };
const MILK_TYPES = {
  entier: "Lait entier",
  poudre: "Poudre de lait",
  mixte: "Mixte (lait entier + poudre)",
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("default", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    : null;

/** The objective only reads as anything once a purpose has been chosen. */
const describeUsage = (calf) => {
  if (!calf.purpose) return null;
  const label = PURPOSES[calf.purpose] || calf.purpose;
  return calf.target_age_months != null
    ? `${label} · ${calf.target_age_months} mois`
    : label;
};

const describeMilkPrice = (calf) => {
  if (calf.milk_price == null) return null;
  return calf.milk_type === "poudre"
    ? `${calf.milk_price} fr / dt`
    : `${calf.milk_price} ct / kg`;
};

const colostrumContents = (calf) =>
  [
    calf.colostrumContent_fer && "Fer",
    calf.colostrumContent_selenium && "Sélénium",
    calf.colostrumContent_locatim && "Locatim",
  ]
    .filter(Boolean)
    .join(", ") || null;

/**
 * The calf's own data, grouped into tabs and, within a tab, into cards.
 *
 * The card is the unit of everything: it is what you see as a bordered block,
 * and it is what you edit. Keeping it that small is what lets a tab hold several
 * subjects without ever putting a long form in front of anyone — "Le veau"
 * carries fourteen fields in total but never more than eight at once.
 *
 * Each group declares both halves, how it reads and which fields edit it, so a
 * new field is added in one place and appears in both.
 */
export const CALF_SECTIONS = [
  {
    id: "veau",
    label: "Le veau",
    groups: [
      {
        id: "identite",
        title: "Identité",
        Fields: CalfIdentityFields,
        rows: (calf) => [
          ["BDTA", calf.bdta],
          ["Nom", calf.name],
          ["Race", calf.breed],
          ["Sexe", calf.sex ? (calf.sex === "m" ? "Mâle" : "Femelle") : null],
          calf.sex === "m"
            ? ["Castration", calf.castration ? "Oui" : "Non"]
            : null,
          ["Future utilisation", describeUsage(calf)],
        ],
      },
      {
        id: "naissance",
        title: "Naissance",
        Fields: ({ form }) => (
          <>
            <CalfBirthFields form={form} />
            <CalfWeightMethodField form={form} />
          </>
        ),
        rows: (calf) => [
          ["Date", formatDate(calf.birthdate)],
          ["Heure", calf.birthtime],
          ["Lieu", calf.birthPlace],
          ["Vitalité", calf.vitality],
          ["Type", calf.birthtype],
          [
            "Méthode de pesée",
            calf.weight_method
              ? calf.weight_method === "estime"
                ? "Estimé"
                : "Mesuré"
              : null,
          ],
        ],
      },
      {
        id: "ecornage",
        title: "Écornage",
        Fields: ({ form }) => <CalfFollowUpFields form={form} showVaccine={false} />,
        rows: (calf) => [
          ["Écornage", calf.ecornage ? "Oui" : "Non"],
          [
            "Date d'écornage",
            calf.ecornage ? formatDate(calf.ecornageDate) : null,
          ],
        ],
      },
    ],
  },
  {
    id: "apport",
    label: "Apport",
    groups: [
      {
        id: "colostrum",
        title: "Colostrum",
        Fields: CalfColostrumFields,
        rows: (calf) => [
          ["Apport", calf.colostrum ? `${calf.colostrum} l` : null],
          ["Heure de l'apport", calf.apporttime],
          ["Moyen d'apport", calf.colostrumMean],
          ["Source", calf.colostrumSource],
          ["Qualité", calf.colostrumQuality],
          ["Contenu", colostrumContents(calf)],
        ],
      },
      {
        id: "allaitement",
        title: "Allaitement",
        Fields: CalfFeedingFields,
        rows: (calf) => [
          ["Type de lait", MILK_TYPES[calf.milk_type] || null],
          [
            "Densité",
            calf.milk_type === "poudre" && calf.milk_density != null
              ? `${calf.milk_density} g/l`
              : null,
          ],
          ["Prix", describeMilkPrice(calf)],
        ],
      },
    ],
    // Aliments are a list with their own add and remove, not fields on the calf,
    // so they render as their own card rather than inside a form.
    Extra: CalfFeeds,
  },
];

const hasValue = (row) =>
  row && row[1] !== null && row[1] !== undefined && row[1] !== "";

/**
 * The rows of one group that actually hold a value.
 *
 * A field the API has never been given comes back undefined rather than null, so
 * both count as unset — otherwise a freshly created calf renders
 * "Race: undefined" and its empty cards never offer to be completed.
 */
export const filledRows = (group, calf) => group.rows(calf).filter(hasValue);

/** True when a card holds nothing and should invite completion instead. */
export const isGroupEmpty = (group, calf) => filledRows(group, calf).length === 0;
